// Local component for visibleScope select, matching OperatorConnectAuthPolicyField style
import { useState } from 'react';

import { axios } from '@/services/axios';
import { InfoIcon } from '@chakra-ui/icons';
import {
    Box,
    FormLabel,
    HStack,
    Skeleton,
    SkeletonCircle,
    Stack,
    Tooltip,
    Text,
    VStack,
    FormControl,
} from '@chakra-ui/react';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    descriptionStyling,
    textBoxStyling,
} from 'components/forms/new/formStyles';
import OperatorConnectNameField from '../edit/formFields/OperatorConnectNameField';
import FormBase from '@/components/forms/new/FormBase';
import { useQuery } from 'hooks/useCustomQuery';
import DropDown from '@/components/forms/new/formikFormFields/DropDown';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Input,
    LoadingIndicator,
} from 'components/v4';
import useFieldValidation from 'hooks/useFieldValidation';
import { isEmpty } from 'lodash';
import walkthroughIds from 'pages/services/walkthroughIds';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { validationSchemaVD } from '../edit/schema';
import { useDefinedAttributes } from './DefinedAttributesContext';
import DefinedAttributes from './attributes/definedAttributes';
import useDefinedAttributeValidation from './attributes/useDefinedAttributeValidation';
import config from '@/config.json';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import BreadcrumbTrail from 'components/BreadcrumbTrail';
import useGetCurrentWholesaler from '@/queries/wholesaler/useGetCurrentWholesaler';
import { resolveWholesalerProfileName } from '../edit/serviceProfileName';
function OperatorConnectAuthPolicyFieldLike({
    value,
    onChange,
    options,
    darkMode,
    error,
    showError,
}) {
    const [isHovered, setIsHovered] = useState(false);
    return (
        <FormControl
            sx={{ margin: '0 !important' }}
            isInvalid={!!error && showError}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={stackStyling(true)}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Visible Scope{' '}
                        <span style={{ color: '#e53e3e' }}>*</span>
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth={250}>
                        Select the visible scope for this profile.
                    </Text>
                </Box>
                <Box flex="1" minWidth="450px">
                    <Box
                        sx={{
                            display: 'flex',
                            width: '100%',
                            alignItems: 'flex-start',
                            paddingTop: '15px',
                        }}>
                        <VStack width="100%" alignItems="normal">
                            <DropDown
                                currentId={value}
                                data={options.map((opt) => ({
                                    id: opt.value,
                                    name: opt.label,
                                }))}
                                shownValue="name"
                                alphabetical={false}
                                handleSelectChange={(item) =>
                                    onChange(item?.id)
                                }
                                darkMode={darkMode}
                                placeholder="Select Visible Scope"
                                width="100%"
                                noPadding={true}
                            />
                            <Text
                                color="red.500"
                                fontSize="xs"
                                mt={1}
                                textAlign="right">
                                {showError && error ? error : ''}
                            </Text>
                        </VStack>
                    </Box>
                </Box>
                {isHovered && (
                    <FieldToolTip
                        label="Determine visibility and service creation level."
                        ariaLabel="vendor-defined-visible-scope-tip"
                        left="25px"
                        top="23px"
                    />
                )}
            </Stack>
        </FormControl>
    );
}
export default function AddVendorDefined({
    data,
    handleBackButton,
    level,
    visibleScopeOptions,
}) {
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(null);
    const [isHovered, setIsHovered] = useState(false);
    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope } = useSelector((state) => state.login);

    const {
        currentWholesaler: wholesalerID,
        currentPartner: partnerID,
        companiesAndPartners,
    } = useSelector((state) => state.navigationLists);
    const { data: currentWholesalerData } = useGetCurrentWholesaler();

    const { dispatch, attributesList, attributesErrors } =
        useDefinedAttributes();

    const DEFAULT_VENDOR_DEFINED_PAYLOAD = {
        name:
            level === 'partner'
                ? 'Vendor Defined Partner Profile'
                : 'Vendor Defined Wholesaler Profile',
        serviceType: 'VendorDefined',
        serviceDefinitionID: '',
        partnerID: level === 'partner' ? partnerID : undefined,
        wholesalerID: level === 'partner' ? undefined : wholesalerID,
        visibleScope: level === 'partner' ? 20 : 60,
    };

    const { ...ufElements } = walkthroughIds.serviceProfiles;

    const [modalState, setModalState] = useState(
        DEFAULT_VENDOR_DEFINED_PAYLOAD,
    );

    const { validationErrors, validatefn } = useFieldValidation(
        modalState,
        validationSchemaVD,
    );

    const { handleAttributeError, removeAttributeError } =
        useDefinedAttributeValidation();

    // get the pre existing data from the backend to display the dynamic attributes
    const { isFetching: vendorDefinedLoading } = useQuery({
        // eg. VendorDefined | the service definition id
        queryKey: ['vendorDefined', data?.serviceType?.split('|')[1]],
        queryFn: async () => {
            const serviceDefinitionID = data?.serviceType?.split('|')[1];
            const res = await axios.get(
                `serviceprofiles/vendordefined/${serviceDefinitionID}/${scope()}`,
                {
                    headers: {
                        'X-RequestScope': requiredScope,
                    },
                },
            );
            return res.data;
        },
        enabled: Boolean(data?.serviceType?.split('|')[1]),
        onSuccess: (data) => {
            // data for the service type and attributes list
            setModalState((prevState) => ({
                ...prevState,
                data,
            }));
            if (data.attributeList?.length > 0) {
                for (let att of data.attributeList) {
                    // attribute value is empty
                    if (isEmpty(att.value)) {
                        // default value is empty, set value to be empty string
                        att.value = isEmpty(att.defaultValue)
                            ? null
                            : att.defaultValue;
                    }
                }
            }
            dispatch({
                type: 'UPDATE_ATT_LIST',
                payload: data.attributeList,
            });
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message);
        },
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (level !== 'partner' && data && currentWholesalerData?.name) {
            handleChangeState({
                name: resolveWholesalerProfileName(
                    currentWholesalerData?.name,
                    data?.displayName,
                ),
            });
        } else if (
            level === 'partner' &&
            data &&
            companiesAndPartners?.length > 0
        ) {
            const partnerName = companiesAndPartners.find(
                ({ id }) => id === partnerID,
            )?.name;
            handleChangeState({
                name: `${partnerName} - ${data?.displayName} Partner Profile`,
            });
        }
    }, [
        data,
        companiesAndPartners,
        currentWholesalerData?.name,
        level,
        wholesalerID,
        partnerID,
    ]);

    /*
     * A function to get the current scope and append it for API calls
     */
    const scope = () => {
        if (level === 'partner') {
            return `Partner/${partnerID}`;
        } else {
            return `Wholesaler/${wholesalerID}`;
        }
    };

    // function to set modal state with a given payload
    const handleChangeState = (payload) => {
        setModalState((prev) => ({
            ...prev,
            ...payload,
        }));
    };

    const handleSubmit = async () => {
        setError(null);
        let hasError = false;

        if (!validatefn()) {
            setError({
                response: {
                    data: {
                        message:
                            'There are some validation errors in the form. Please check and try again.',
                    },
                },
            });
            return;
        }

        // check for any attributes errors
        if (attributesErrors.length > 0) {
            hasError = true;
        }

        // check for required attributes
        if (attributesList?.length > 0) {
            attributesList.forEach((att, index) => {
                const validation = JSON.parse(
                    att.definedAttributeValidationType,
                );

                // if required, input cannot be empty
                if (validation.Required && isEmpty(att.value)) {
                    handleAttributeError(index, 'Field is required.');
                    hasError = true;
                } else {
                    removeAttributeError(index);
                }
            });
        }
        if (hasError) return;

        setIsLoading(true);
        try {
            const serviceDefinitionID = data?.serviceType?.split('|')[1];
            // wholesaler creates a service profile for partner
            if (level === 'partner') {
                console.log('this is partner add vendor');
                await axios.post(
                    `serviceprofiles/vendordefined/${serviceDefinitionID}/partner`,
                    {
                        name: modalState.name,
                        partnerID: modalState.partnerID,
                        serviceDefinitionID: serviceDefinitionID,
                        visibleScope: modalState.visibleScope,
                        attributeList: attributesList,
                    },
                    {
                        headers: {
                            'X-RequestScope': requiredScope,
                        },
                    },
                );
            }
            // GA creates for wholesaler
            else {
                console.log('this is wholesaler add vendor');
                await axios.post(
                    `serviceprofiles/vendordefined/${serviceDefinitionID}/wholesaler`,
                    {
                        name: modalState.name,
                        wholesalerID: modalState.wholesalerID,
                        serviceDefinitionID: serviceDefinitionID,
                        visibleScope: modalState.visibleScope,
                        attributeList: attributesList,
                    },
                    {
                        headers: {
                            'X-RequestScope': requiredScope,
                        },
                    },
                );
            }
            toast.success(
                `Successfully created a ${
                    level === 'partner' ? 'Partner' : 'Wholesaler'
                } Vendor Defined Service Profile!`,
            );
            handleBackButton();
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }
    const breadCrumbTrail = [
        { label: 'Services', path: ['', { index: 0 }] },
        { label: 'Configurations', path: '' },
    ];
    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`Add ${modalState.name}`}
                subTitleHeader={true}
                backLink={['/service-profiles', { state: { index: 0 } }]}
                initialValues={DEFAULT_VENDOR_DEFINED_PAYLOAD}
                ufBackBtn="builderBackBtn"
                ufSaveBtn="builderSaveBtn"
                customClick={true}
                onBack={handleBackButton}
                isLoading={isLoading}
                finalFormSubmit={handleSubmit}>
                {() => (
                    <>
                        {error && (
                            <Alert status="error" sx={{ marginTop: '1rem' }}>
                                <AlertIcon />
                                <AlertDescription>
                                    {error?.response?.data?.message ||
                                        'An error occured. Please try again later.'}
                                </AlertDescription>
                            </Alert>
                        )}
                        {/* Service Name Field - OperatorConnectNameField for perfect parity */}
                        <FormControl
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                            isRequired={true}
                            isInvalid={!!validationErrors?.name}
                            sx={{ margin: '0 !important' }}>
                            <Stack
                                sx={stackStyling(true)}
                                spacing={stackSpacing}
                                alignItems="flex-start"
                                direction="row">
                                <Box>
                                    <FormLabel sx={formLabelStyling()}>
                                        Service name
                                    </FormLabel>
                                    <Text
                                        sx={descriptionStyling}
                                        maxWidth={250}>
                                        Enter a name for this Vendor Defined
                                        profile.
                                    </Text>
                                </Box>
                                <Box style={{ minWidth: '450px' }}>
                                    <VStack width="100%" alignItems="normal">
                                        <Input
                                            id="service-name"
                                            name="serviceName"
                                            placeholder="Please enter a value"
                                            value={modalState.name}
                                            onChange={(e) =>
                                                handleChangeState({
                                                    name: e.target.value,
                                                })
                                            }
                                            sx={{
                                                ...textBoxStyling,
                                                marginTop: '18px',
                                                paddingTop: 0,
                                            }}
                                            data-walkthroughid={
                                                ufElements.ufServiceNameInput
                                            }
                                        />
                                    </VStack>
                                </Box>
                                {isHovered && (
                                    <FieldToolTip
                                        label="enter the service name"
                                        ariaLabel="vendor-defined-list-attribute-tip"
                                        left="25px"
                                        top="23px"
                                    />
                                )}
                            </Stack>
                        </FormControl>
                        {/* Visible Scope Field - OperatorConnect style */}
                        <FormControl
                            isRequired={true}
                            sx={{ margin: '0 !important', mt: 4 }}>
                            <Stack
                                direction="row"
                                spacing={stackSpacing}
                                sx={stackStyling(true)}>
                                {/* <Box>
                                <FormLabel sx={formLabelStyling(darkMode)}>
                                    Visible Scope <span style={{ color: '#e53e3e' }}>*</span>
                                    <Tooltip
                                        label="Determine visibility and service creation level"
                                        hasArrow
                                        placement="top-end">
                                        <InfoIcon />
                                    </Tooltip>
                                </FormLabel>
                                <Text sx={descriptionStyling} maxWidth={250}>
                                    Select the visible scope for this profile.
                                </Text>
                            </Box> */}
                                <Box flex="1" minWidth="450px">
                                    <VStack width="100%" alignItems="normal">
                                        {modalState.visibleScope <=
                                        requiredScope ? (
                                            <OperatorConnectAuthPolicyFieldLike
                                                value={modalState.visibleScope}
                                                onChange={(v) =>
                                                    handleChangeState({
                                                        visibleScope: v,
                                                    })
                                                }
                                                options={visibleScopeOptions}
                                                darkMode={darkMode}
                                                error={
                                                    validationErrors?.visibleScope
                                                }
                                                showError={
                                                    !!validationErrors?.visibleScope
                                                }
                                            />
                                        ) : (
                                            <HStack
                                                alignItems="flex-start"
                                                width="100%">
                                                <OperatorConnectNameField
                                                    darkMode={darkMode}
                                                    value={
                                                        config.scope.json[
                                                            modalState
                                                                .visibleScope
                                                        ]
                                                    }
                                                    onChange={(e) =>
                                                        handleChangeState({
                                                            name: e.target
                                                                .value,
                                                        })
                                                    }
                                                    error={
                                                        validationErrors?.name
                                                    }
                                                    showError={
                                                        !!validationErrors?.name
                                                    }
                                                    data-walkthroughid={
                                                        ufElements.ufServiceNameInput
                                                    }
                                                />
                                                <Text
                                                    as="span"
                                                    sx={{
                                                        width: '100%',
                                                        padding: '8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #e2e8f0',
                                                        background: '#f9f9f9',
                                                        color: '#4A5568',
                                                        fontSize: '1rem',
                                                    }}>
                                                    {
                                                        config.scope.json[
                                                            modalState
                                                                .visibleScope
                                                        ]
                                                    }
                                                </Text>
                                                <Tooltip
                                                    label="Determine visibility and service creation level"
                                                    hasArrow
                                                    placement="top-end">
                                                    <InfoIcon />
                                                </Tooltip>
                                            </HStack>
                                        )}
                                    </VStack>
                                </Box>
                            </Stack>
                        </FormControl>
                        {vendorDefinedLoading ? (
                            <Box>
                                {[...Array(3)].map((_, index) => (
                                    <Stack key={index} mb={3}>
                                        <HStack>
                                            <Skeleton h={5} w="25%" />
                                            <SkeletonCircle h={5} w={5} />
                                        </HStack>
                                        <Skeleton h={10} borderRadius={4} />
                                        <Skeleton h={5} w="15%" />
                                    </Stack>
                                ))}
                            </Box>
                        ) : (
                            <DefinedAttributes requiredScope={requiredScope} />
                        )}
                    </>
                )}
            </FormBase>
        </>
    );
}
